import React, { AnchorHTMLAttributes, ButtonHTMLAttributes } from 'react';

import Link from 'app-migration/components/link/Link';
import clsx from 'clsx';
import { createUseStyles } from 'react-jss';

import { Spacing } from 'DesignSystem2/Theme';
import Padding from 'DesignSystem2/Theme/Padding';
import { noop } from 'utils/noop';

import { Icon } from '../Icon';
import { IconName } from '../Icon';
import Text from '../Text';
import Palette from '../Theme/Palette';

const useStyles = createUseStyles({
    textButton: {
        display: 'inline-flex',
        justifyContent: 'center',
        alignItems: 'center',
        boxSizing: 'border-box',
        cursor: 'pointer',
        border: 'none',
        backgroundColor: 'transparent',
        padding: [Spacing(0.5), 0],
        minHeight: 32,
    },
});

const useStateStyles = createUseStyles({
    enabled: {
        color: Palette.Fill.x10,
        transition: 'color 83ms',
        '&:hover': {
            color: Palette.Fill.x20,
        },
        '&:active': {
            color: Palette.Fill.x20,
        },
    },
    disabled: { color: Palette.Fill.x30 },
});

interface BaseTextButtonProps {
    text: string;
    icon?: IconName;
    disabled?: boolean;
    onClick?: (event: React.MouseEvent<Element, MouseEvent>) => void;
}

interface LinkTextButtonProps extends BaseTextButtonProps {
    newTab?: boolean;
    buttonProps?: never;
    anchorProps?: Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>;
}
type InternalLinkTextButtonProps = LinkTextButtonProps & { internalHref: string; externalHref?: never };
type ExternalLinkTextButtonProps = LinkTextButtonProps & { internalHref?: never; externalHref: string };

interface ButtonTextButtonProps extends BaseTextButtonProps {
    internalHref?: never;
    externalHref?: never;
    newTab?: never;
    buttonProps?: ButtonHTMLAttributes<HTMLButtonElement>;
    anchorProps?: never;
}

type TextButtonProps = InternalLinkTextButtonProps | ExternalLinkTextButtonProps | ButtonTextButtonProps;

const TextButton = ({
    text,
    icon,
    disabled = false,
    onClick = noop,
    internalHref,
    externalHref,
    newTab = false,
    buttonProps,
    anchorProps,
}: TextButtonProps): JSX.Element => {
    const classes = useStyles();
    const stateClasses = useStateStyles();

    const IconElement = !!icon && Icon[icon];
    const sizedIconElement = !!IconElement && <IconElement size={'16px'} />;

    const processedText = text + (icon ? '' : ' →');
    const textElement = <Text.Emphasis.x20>{processedText}</Text.Emphasis.x20>;

    const selectedClasses = clsx(classes.textButton, disabled ? stateClasses.disabled : stateClasses.enabled);
    const content = (
        <>
            {textElement}
            {sizedIconElement && <Padding spacing={0.5} />}
            {sizedIconElement}
        </>
    );

    return !!internalHref ? (
        <Link
            onClick={onClick}
            to={internalHref}
            {...(newTab ? { target: '_blank', rel: 'noopener noreferrer' } : undefined)}
            className={selectedClasses}
            {...anchorProps}
        >
            {content}
        </Link>
    ) : !!externalHref ? (
        <a
            onClick={onClick}
            href={externalHref}
            {...(newTab ? { target: '_blank', rel: 'noopener noreferrer' } : undefined)}
            className={selectedClasses}
            {...anchorProps}
        >
            {content}
        </a>
    ) : (
        <button onClick={onClick} disabled={disabled} className={selectedClasses} {...buttonProps}>
            {content}
        </button>
    );
};

export default TextButton;
